import ComponentWithSocket from '../../../lib/component';
import { Component, Watch } from 'vue-property-decorator';
import moment from 'moment';

import * as Stock from '../../../utils/stock';
import { depositorName } from '../../../utils/depositor';
import { QrCamera } from '../../../utils/qr-camera';
import {
    ScanTone,
    enableScanSound,
    scanSignal,
} from '../../../utils/scan-signal';

const SESSION_KEY = 'stock_scan_session';

const MAX_SCANS = 500;

// Cererea se pierde fara raspuns daca socketul e deconectat (ws.ts nu trimite
// nimic cand conexiunea e inchisa), iar coada nu trebuie sa ramana blocata.
const REQUEST_TIMEOUT_MS = 15000;

/**
 * Ce face fiecare scanare. Notele sunt doar sugestii pentru campul de nota,
 * care se aplica tuturor scanarilor pana e schimbat.
 */
const MODES: {
    [mode: string]: { label: string; inStock?: boolean; notes: string[] };
} = {
    view: { label: 'Consultare', notes: [] },
    in: {
        label: 'Intrare in stoc',
        inStock: true,
        notes: [
            'Primit de la deponent',
            'Revenit de la restaurare',
            'Revenit din expozitie',
        ],
    },
    out: {
        label: 'Iesire din stoc',
        inStock: false,
        notes: [
            'Predat castigatorului',
            'Returnat deponentului',
            'Plecat la restaurare',
            'Plecat la expozitie',
        ],
    },
};

const RESULTS: {
    [status: string]: { label: string; badge: string; tone?: ScanTone };
} = {
    pending: { label: 'Se cauta...', badge: 'is-unknown' },
    in: { label: 'Intrat in stoc', badge: 'is-in', tone: 'ok' },
    out: { label: 'Iesit din stoc', badge: 'is-out', tone: 'ok' },
    viewed: { label: 'Gasit', badge: 'is-unknown', tone: 'ok' },
    unchanged: { label: 'Neschimbat', badge: 'is-warn', tone: 'warn' },
    duplicate: { label: 'Cod duplicat', badge: 'is-warn', tone: 'warn' },
    'not-found': { label: 'Negasit', badge: 'is-error', tone: 'error' },
    invalid: { label: 'Cod necunoscut', badge: 'is-error', tone: 'error' },
    error: { label: 'Eroare', badge: 'is-error', tone: 'error' },
};

const PROBLEMS = ['duplicate', 'not-found', 'invalid', 'error'];

/** Ce pastram din lot in lista sesiunii (si in sessionStorage). */
function scannedLot(lot: any) {
    const image = lot.imagesData && lot.imagesData[0];

    return {
        _id: lot._id,
        sku: lot.sku,
        name: lot.name,
        author: lot.author && lot.author.name,
        thumbnail: image && image.thumbnail,
        image: image && image.full,
        inStock: lot.inStock,
        sold: lot.sold,
        isUnavailable: lot.isUnavailable,
        depositor: depositorName(lot.depositor),
        contract: Stock.formatContractNumber(lot.contract),
    };
}

/**
 * Scanare continua a etichetelor din depozit: in modul ales, fiecare lot
 * scanat este doar afisat, pus in stoc sau scos din stoc. Lucreaza peste
 * aceleasi evenimente ca pagina lotului, deci aceleasi reguli si acelasi
 * istoric de miscari.
 *
 * Scanarile se proceseaza pe rand: raspunsul de eroare la cautare nu spune
 * pentru ce cod a venit, deci doar ordinea le leaga de scanare.
 */
@Component
export default class StockScan extends ComponentWithSocket {
    public data: any = {
        mode: 'view',
        note: '',
        manualCode: '',
        camera: {
            on: false,
            starting: false,
            error: '',
            torchAvailable: false,
            torchOn: false,
        },
        // Cele mai noi primele.
        scans: [],
        // Scanarile care asteapta raspuns, in ordinea in care au fost facute.
        queue: [],
    };

    // Nedeclarate in `data`: nu trebuie sa fie reactive.
    private camera?: QrCamera;

    private requestTimer?: number;

    formatSku = Stock.formatSku;

    constructor() {
        super();

        this.wsListener.onGet('stock.lot', (success: boolean, data: any) =>
            this.onLotFound(success, data),
        );

        this.wsListener.onPut('stock.lot', (success: boolean, data: any) =>
            this.onStockSaved(success, data),
        );
    }

    mounted() {
        this.restoreSession();

        this.camera = new QrCamera(this.$refs.video as HTMLVideoElement, code =>
            this.handleCode(code),
        );
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('touchend', enableScanSound);

        // Cititoarele de coduri cu fir sau Bluetooth scriu ca o tastatura in
        // campul activ. Pe telefon nu deschidem tastatura din oficiu.
        if (window.matchMedia('(pointer: fine)').matches) {
            (this.$refs.codeInput as HTMLInputElement).focus();
        }
    }

    beforeDestroy() {
        document.removeEventListener(
            'visibilitychange',
            this.onVisibilityChange,
        );
        document.removeEventListener('touchend', enableScanSound);
        window.clearTimeout(this.requestTimer);
        if (this.camera) this.camera.stop();
    }

    beforeRouteLeave(to: any, from: any, next: any) {
        this._beforeLeave(to, from);
        next();
    }

    @Watch('data.note')
    onNoteChanged() {
        this.saveSession();
    }

    get modes() {
        return MODES;
    }

    get modeNotes(): string[] {
        return MODES[this.data.mode].notes;
    }

    get summary() {
        const count = (statuses: string[]) =>
            this.data.scans.filter(
                (scan: any) => statuses.indexOf(scan.status) !== -1,
            ).length;

        return {
            total: this.data.scans.length,
            in: count(['in']),
            out: count(['out']),
            unchanged: count(['unchanged']),
            problems: count(PROBLEMS),
        };
    }

    get lastScan() {
        return this.data.scans[0];
    }

    setMode(mode: string) {
        enableScanSound();
        this.data.mode = mode;
        this.data.note = '';
        this.saveSession();
    }

    async toggleCamera() {
        enableScanSound();
        const camera = this.data.camera;

        if (camera.on) {
            this.camera!.stop();
            camera.on = false;
            camera.torchOn = false;
            return;
        }

        camera.on = true;
        camera.starting = true;
        camera.error = '';

        try {
            await this.camera!.start();
            camera.torchAvailable = this.camera!.torchAvailable;
        } catch (error) {
            camera.on = false;
            camera.error = error.message;
        } finally {
            camera.starting = false;
        }
    }

    async toggleTorch() {
        const next = !this.data.camera.torchOn;
        await this.camera!.setTorch(next).catch(() => undefined);
        this.data.camera.torchOn = next;
    }

    // iOS opreste camera cand pagina trece in fundal: o eliberam noi si o
    // repornim la revenire.
    onVisibilityChange() {
        if (!this.data.camera.on || !this.camera) return;

        if (document.visibilityState === 'hidden') {
            this.camera.stop();
            this.data.camera.torchOn = false;
            return;
        }

        this.camera.start().catch((error: Error) => {
            this.data.camera.on = false;
            this.data.camera.error = error.message;
        });
    }

    submitCode() {
        enableScanSound();
        const code = this.data.manualCode;
        this.data.manualCode = '';

        if (code.trim()) {
            this.handleCode(code);
        }
    }

    handleCode(code: string) {
        const sku = Stock.skuFromCode(code);
        const scan = {
            id: `${Date.now()}-${Math.random()}`,
            sku,
            mode: this.data.mode,
            note: this.data.note.trim(),
            status: 'pending',
            message: '',
            lot: undefined as any,
            at: Date.now(),
        };

        if (sku === undefined) {
            this.data.scans.unshift(scan);
            return this.finish(scan, 'invalid', code);
        }

        // Aceeasi eticheta tinuta in fata camerei cat inca se cauta.
        if (this.data.queue.some((queued: any) => queued.sku === sku)) {
            return;
        }

        this.data.scans.unshift(scan);
        this.data.queue.push(scan);

        if (this.data.queue.length === 1) {
            this.sendNext();
        }
    }

    sendNext() {
        const scan = this.data.queue[0];
        if (!scan) return;

        this.armTimeout();
        this.wsListener.sendGet('stock.lot', { sku: scan.sku });
    }

    armTimeout() {
        window.clearTimeout(this.requestTimer);
        this.requestTimer = window.setTimeout(() => {
            const scan = this.data.queue[0];
            if (scan) {
                this.finish(
                    scan,
                    'error',
                    'Serverul nu a raspuns. Verifica conexiunea si scaneaza din nou.',
                );
            }
        }, REQUEST_TIMEOUT_MS);
    }

    onLotFound(success: boolean, data: any) {
        const scan = this.data.queue[0];
        if (!scan || scan.status !== 'pending' || scan.lot) return;

        if (!success) {
            return this.finish(scan, 'not-found', data);
        }

        if (data.matches) {
            return this.finish(
                scan,
                'duplicate',
                `${data.matches.length} loturi au acest cod. Deschide lotul si alege-l pe cel corect.`,
            );
        }

        // Raspuns intarziat pentru o scanare abandonata la timeout.
        if (data.lot.sku !== scan.sku) return;

        scan.lot = scannedLot(data.lot);

        const inStock = MODES[scan.mode].inStock;
        if (inStock === undefined) {
            return this.finish(scan, 'viewed');
        }

        const allowed = inStock
            ? Stock.canSetInStock(data.lot)
            : Stock.canSetOutOfStock(data.lot);

        if (!allowed) {
            return this.finish(
                scan,
                'unchanged',
                inStock ? 'Era deja in stoc' : 'Era deja iesit din stoc',
            );
        }

        this.armTimeout();
        this.wsListener.sendPut('stock.lot', {
            _id: data.lot._id,
            inStock,
            note: scan.note,
        });
    }

    onStockSaved(success: boolean, data: any) {
        const scan = this.data.queue[0];
        if (!scan || scan.status !== 'pending' || !scan.lot) return;

        if (!success) {
            return this.finish(scan, 'error', data);
        }

        if (String(data.lot._id) !== String(scan.lot._id)) return;

        scan.lot = scannedLot(data.lot);
        this.finish(scan, scan.mode);
    }

    finish(scan: any, status: string, message: string = '') {
        scan.status = status;
        scan.message = message;

        const tone = RESULTS[status].tone;
        if (tone) scanSignal(tone);

        const index = this.data.queue.indexOf(scan);
        if (index !== -1) {
            window.clearTimeout(this.requestTimer);
            this.data.queue.splice(index, 1);
            this.sendNext();
        }

        this.saveSession();
    }

    clearScans() {
        if (
            !confirm(
                'Golesti lista de scanari? Miscarile de stoc raman salvate in istoricul loturilor.',
            )
        ) {
            return;
        }

        this.data.scans = this.data.scans.filter(
            (scan: any) => scan.status === 'pending',
        );
        this.saveSession();
    }

    resultLabel(scan: any): string {
        return scan.status === 'viewed' && scan.lot
            ? Stock.stockLabel(scan.lot)
            : RESULTS[scan.status].label;
    }

    resultBadge(scan: any): string {
        return scan.status === 'viewed' && scan.lot
            ? Stock.stockBadgeClass(scan.lot)
            : `badge-stoc ${RESULTS[scan.status].badge}`;
    }

    resultTone(scan: any): string {
        return `is-${RESULTS[scan.status].tone || 'pending'}`;
    }

    // Lotul se deschide dupa SKU: pentru un cod duplicat, pagina lotului
    // ofera alegerea.
    canOpen(scan: any): boolean {
        return !!scan.lot || scan.status === 'duplicate';
    }

    formatTime(at: number): string {
        return moment(at).format('HH:mm:ss');
    }

    /**
     * Lista ramane la navigarea catre un lot si inapoi (sessionStorage e pe
     * tab). Scanarile neterminate nu se salveaza: raspunsul lor se pierde.
     */
    saveSession() {
        sessionStorage.setItem(
            SESSION_KEY,
            JSON.stringify({
                mode: this.data.mode,
                note: this.data.note,
                scans: this.data.scans
                    .filter((scan: any) => scan.status !== 'pending')
                    .slice(0, MAX_SCANS),
            }),
        );
    }

    restoreSession() {
        try {
            const session = JSON.parse(
                sessionStorage.getItem(SESSION_KEY) || 'null',
            );
            if (!session) return;

            this.data.mode = MODES[session.mode] ? session.mode : 'view';
            this.data.note = session.note || '';
            this.data.scans = session.scans || [];
        } catch (e) {
            // Sesiune corupta - pornim cu lista goala.
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/stock/scan/index.ts